import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, Button, Table, Spinner, Badge } from 'reactstrap';
import { Icon } from '@iconify/react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const SupplierStatements = ({ onPay, onLinkBank }) => {
    const [statements, setStatements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedSupplier, setExpandedSupplier] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchStatements = async () => {
        try {
            setLoading(true);
            const response = await api.get('/expenses/supplier-statements');
            if (response.data.success) {
                const sorted = response.data.data.sort((a,b) => b.total_balance - a.total_balance);
                setStatements(sorted.filter(s => s.total_balance > 0));
            }
        } catch (error) {
            console.error('Error fetching statements:', error);
            toast.error('Error obteniendo estado de cuentas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatements();
    }, []);

    const toggleSupplier = (id) => {
        if (expandedSupplier === id) setExpandedSupplier(null);
        else setExpandedSupplier(id);
    };

    const handleInvoiceAction = async (invoice, actionType) => {
        try {
            setActionLoading(true);
            const pref = invoice.due?.prefix || 'FC';
            const cons = invoice.due?.consecutive;
            const dueDate = invoice.due?.date;
            const siigoBalance = invoice.due?.balance; // Live Siigo Balance
            if (!cons) {
                toast.error('No se puede identificar el consecutivo de la factura en Siigo.');
                return;
            }
            const fcNumber = `${pref}-1-${cons}`.toUpperCase();

            let expenseData = null;

            // 1. Try to find locally
            const response = await api.get(`/expenses/by-fc/${fcNumber}`);
            if (response.data.success && response.data.expense) {
                expenseData = response.data.expense;
            } else {
                // 2. Auto-import if not found
                toast.loading(`Auto-importando factura ${fcNumber} desde Siigo...`, { id: 'auto-import-toast' });
                const importRes = await api.post('/expenses/auto-import', { fc: fcNumber, due_date: dueDate });
                toast.dismiss('auto-import-toast');
                
                if (importRes.data.success && importRes.data.expense) {
                    toast.success('Factura auto-importada correctamente.');
                    expenseData = importRes.data.expense;
                } else {
                    toast.error(importRes.data.message || `No se pudo encontrar ni autolocalizar la factura ${fcNumber}.`);
                    return;
                }
            }

            // Inyectar el siigoBalance en vivo para que el modal lo lea
            if (expenseData) {
                expenseData.live_siigo_balance = siigoBalance;
            }

            if (actionType === 'pay' && onPay) {
                onPay(expenseData);
            } else if (actionType === 'link' && onLinkBank) {
                onLinkBank(expenseData);
            }

        } catch (error) {
            toast.dismiss('auto-import-toast');
            console.error('Error locating expense:', error);
            toast.error('Error al localizar la factura en la base de datos.');
        } finally {
            setActionLoading(false);
        }
    };

    const currencyFormatter = new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 2
    });

    const grandTotal = statements.reduce((sum, stmt) => sum + stmt.total_balance, 0);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Sincronizando cuentas por pagar con Siigo...</p>
            </div>
        );
    }

    return (
        <Card className="shadow">
            <CardHeader className="bg-white border-0 d-flex justify-content-between align-items-center flex-wrap">
                <div className="mb-2 mb-md-0 d-flex flex-column">
                    <h3 className="mb-0">
                        <Icon icon="mdi:account-cash" className="mr-2 text-info"/>
                        Consolidado de Cuentas por Pagar (Proveedores)
                    </h3>
                    {statements.length > 0 && (
                        <h4 className="mt-2 text-danger font-weight-bold">
                            Total Cuentas por Pagar: {currencyFormatter.format(grandTotal)}
                        </h4>
                    )}
                </div>
                <Button color="info" size="sm" onClick={fetchStatements}>
                    <Icon icon="mdi:refresh" /> Sincronizar Cuentas
                </Button>
            </CardHeader>
            <CardBody className="p-0">
                <Table className="align-items-center table-flush table-hover" responsive>
                    <thead className="thead-light">
                        <tr>
                            <th style={{width: '40%'}}>Proveedor</th>
                            <th>NIT / Cédula</th>
                            <th>Saldo Pendiente en Siigo</th>
                            <th>Facturas Vivas</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {statements.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center py-4">No hay saldos pendientes en proveedores.</td>
                            </tr>
                        ) : (
                            statements.map((stmt, idx) => (
                                <React.Fragment key={idx}>
                                    <tr onClick={() => toggleSupplier(stmt.provider.identification)} style={{cursor: 'pointer'}} className={expandedSupplier === stmt.provider.identification ? "bg-light" : ""}>
                                        <td className="font-weight-bold text-dark">
                                            <Icon icon={expandedSupplier === stmt.provider.identification ? "mdi:chevron-down" : "mdi:chevron-right"} className="mr-2"/>
                                            {stmt.provider.name}
                                        </td>
                                        <td>{stmt.provider.identification}</td>
                                        <td className="font-weight-bold text-danger text-lg">
                                            {currencyFormatter.format(stmt.total_balance)}
                                        </td>
                                        <td>
                                            <Badge color="info">{stmt.invoices.length} Dctos</Badge>
                                        </td>
                                        <td>
                                            <Button color="primary" size="sm" outline onClick={(e) => { e.stopPropagation(); toggleSupplier(stmt.provider.identification); }}>
                                                Ver Detalle
                                            </Button>
                                        </td>
                                    </tr>
                                    {expandedSupplier === stmt.provider.identification && (
                                        <tr>
                                            <td colSpan="5" className="p-0 border-0">
                                                <div className="p-3 bg-secondary">
                                                    <h6 className="text-muted text-uppercase mb-3 mt-1">
                                                        Detalle de Documentos Vencidos (Saldos Mágicos Siigo)
                                                    </h6>
                                                    <Table size="sm" className="bg-white rounded shadow-sm">
                                                        <thead className="thead-light">
                                                            <tr>
                                                                <th>Prefijo</th>
                                                                <th>Consecutivo</th>
                                                                <th>Fecha Vcto</th>
                                                                <th>Saldo Restante</th>
                                                                <th className="text-right">Tomar Acción</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {stmt.invoices.map((inv, iIdx) => (
                                                                <tr key={iIdx}>
                                                                    <td className="font-weight-bold">{inv.due?.prefix || '-'}</td>
                                                                    <td>{inv.due?.consecutive || '-'}</td>
                                                                    <td>{inv.due?.date || '-'}</td>
                                                                    <td className="text-danger font-weight-bold">{currencyFormatter.format(inv.due?.balance || 0)}</td>
                                                                    <td className="text-right">
                                                                        <Button 
                                                                            color="success" 
                                                                            size="sm" 
                                                                            className="mr-2"
                                                                            onClick={() => handleInvoiceAction(inv, 'pay')}
                                                                            disabled={actionLoading}
                                                                            title="Registrar un pago (Caja Menor, MercadoPago, etc.)"
                                                                        >
                                                                            <Icon icon="mdi:cash-check" className="mr-1"/> Saldar
                                                                        </Button>
                                                                        <Button 
                                                                            color="warning" 
                                                                            size="sm" 
                                                                            onClick={() => handleInvoiceAction(inv, 'link')}
                                                                            disabled={actionLoading}
                                                                            title="Vincular un pago huérfano de Bancolombia"
                                                                        >
                                                                            <Icon icon="mdi:bank-transfer" className="mr-1"/> Vincular Banco
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </Table>
            </CardBody>
        </Card>
    );
};

export default SupplierStatements;
